import { each, extend } from 'lodash-es'
import Vue from 'vue'
import Router from 'vue-router'

import store from '@/store'
import clientHTTP from '@/modules/clientHTTP'
import eventBus from '@/modules/eventBus'
import auth from '@/api/auth'

// Login routes
import loginRoutes from './login'

// Common routes
import mainRoutes from './main'
import Squeleton from '@/components/layout/Squeleton'
import Error from '@/components/page/errors/Error'
import Home from '@/components/page/home/Home'

import managementRoutes from './management'
import alarmsRoutes from './alarms'
import selectionsRoutes from './selections'
import workflowsRoutes from './workflows'
import adminRoutes from './admin'

Vue.use(Router)

// ********** META PROPERTIES ********** //
// https://router.vuejs.org/en/advanced/meta.html
// ***** AUTHENTICATION
// To restrict a route to an authenticated user, set the property 'requiresAuth' to true
// ***** ROLES
// To restrict a route to specific roles, add a meta property named 'roles'
// It can be an array of roles (['SUPER_ADMIN', 'ADMIN'] for exemple) to restrict to specific roles
// or '*' to allow all roles. If no 'roles' property exists, loop throught the parents routes
// and check the first found 'roles' property.
// If no 'roles' property exists until the root route, it is '*' by default.

const router = new Router({
  linkActiveClass: 'active',

  // Reset scroll if no savedPosition is saved
  // Do not reset if we are on the same page, but with different parameters (page, pageSize...)
  // https://router.vuejs.org/guide/advanced/scroll-behavior.html
  scrollBehavior (to, from, savedPosition) {
    if (to.path === from.path) return

    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },

  routes: [
    ...loginRoutes,
    {
      path: '/',
      component: Squeleton,
      meta: {
        requiresAuth: true
      },
      children: [
        {
          // Home page
          path: '',
          component: Home
        },
        ...mainRoutes,
        ...addMetaMenu(managementRoutes, 'management'),
        ...addMetaMenu(alarmsRoutes, 'alarms'),
        ...selectionsRoutes,
        ...addMetaMenu(workflowsRoutes, 'workflows'),
        ...addMetaMenu(adminRoutes, 'administration'),
        {
          // 403 Forbidden
          path: '403',
          component: Error,
          meta: {
            code: 403
          }
        }, {
          // 404 Not Found
          path: '*',
          component: Error,
          meta: {
            code: 404
          }
        }
      ]
    }
  ]
})

function addMetaMenu (routes, menu) {
  each(routes, route => {
    if (!route.meta) route.meta = {}
    extend(route.meta, { menu })

    if (route.children) addMetaMenu(route.children, menu)
  })

  return routes
}

router.beforeEach((to, from, next) => {
  // Cancel all ongoing async requests
  clientHTTP.clearAsync()
  // Clear 403/404 error screens
  eventBus.$emit('request:clear')

  // Hide scroll to top button if there is a floatin button
  if (to.matched.some(route => route.meta.hasFloatingButton)) store.commit('setHasFloatingButton', true)
  else store.commit('setHasFloatingButton', false)

  // Check for existing fleets and clusters
  if (to.matched.some(route => route.meta.checkFleets)) store.dispatch('checkUserHasFleets')
  if (to.matched.some(route => route.meta.checkClusters)) store.dispatch('checkUserHasClusters')

  // Clear filters if changing areas (ex: clear gateways filters if not in gateways pages)
  if (to.path.indexOf('gateways') === -1) store.commit('resetGatewaysFilters')
  if (to.path.indexOf('end-devices') === -1) store.commit('resetEndDevicesFilters')
  if (to.path.indexOf('multicast-groups') === -1) store.commit('resetMulticastGroupsFilters')

  // Loggued in > no login screen
  if (to.matched.some(route => (route.path === '/login')) && auth.isAuthenticated()) {
    next('/')
    return
  }

  if (to.matched.some(route => route.meta.requiresAuth)) {
    // if route requires auth and user isn't authenticated => redirect to login page
    if (!auth.isAuthenticated()) {
      // Logout without routing to use next (needed to change page)
      auth.logout(null, true)
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
      return
    }
  }

  // Default
  next()
})

export default router
